// client/src/components/ClientPromo.jsx

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { firestore, storage } from '../../firebase';
import { collection, query, getDocs, doc, onSnapshot, getDoc, limit as fsLimit } from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { ref, listAll, list, getDownloadURL, getMetadata } from 'firebase/storage';
import '../CSS/Dashboard.css';
import { useNavigate } from 'react-router-dom';
import ClientNavbar from './ClientNavbar';

/* ============================
   CONFIG (tuning knobs)
============================ */
const LOGO_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const URL_TTL_MS = 24 * 60 * 60 * 1000;      // 24h pour cache d'URL de fichiers (getDownloadURL)
const META_TTL_MS = 24 * 60 * 60 * 1000;     // 24h pour cache de metadata (timeCreated, contentType)
const PROMO_INDEX_TTL_LOCAL_MS = 6 * 60 * 60 * 1000; // 6h TTL pour le cache localStorage
const PROMO_INDEX_CACHE_KEY = (uid) => `promo_index_${uid}`;
const ENTREPRISE_LIST_LIMIT = 50; // Firestore rules: list requires limit <= 50

/* ============================
   Helpers
============================ */
const nowMs = () => Date.now();

const kindFromContentType = (ct = '') => {
  if (!ct) return null;
  if (ct.startsWith('image/')) return 'image';
  if (ct === 'application/pdf') return 'pdf';
  if (ct.startsWith('video/')) return 'video';
  return 'other';
};
const kindFromUrlExt = (url = '') => {
  const lower = url.toLowerCase();
  if (lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.webp')) return 'image';
  if (lower.endsWith('.pdf')) return 'pdf';
  if (lower.endsWith('.mp4')) return 'video';
  return 'other';
};

const Thumb = React.memo(function Thumb({ pub }) {
  const kind = useMemo(() => {
    const byMeta = kindFromContentType(pub.contentType);
    return byMeta || kindFromUrlExt(pub.url);
  }, [pub.contentType, pub.url]);

  const mediaStyle = {
    width: '100%',
    height: '100%',
    borderRadius: 0,
    objectFit: 'cover',
    display: 'block',
    pointerEvents: 'none',
  };

  if (kind === 'image') {
    return <img loading="lazy" src={pub.url} alt={pub.name || 'Promotion'} style={mediaStyle} />;
  }
  if (kind === 'video') {
    return <video src={pub.url} muted playsInline preload="metadata" style={mediaStyle} />;
  }
  if (kind === 'pdf') {
    return (
      <iframe
        title={pub.name || 'PDF'}
        src={`${pub.url}#zoom=page-fit&scrollbar=0&toolbar=0&navpanes=0`}
        loading="lazy"
        style={{ ...mediaStyle, border: 'none', background: '#fff' }}
      />
    );
  }
  return (
    <div
      style={{
        ...mediaStyle,
        background: 'var(--eb-surface-2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        color: 'var(--eb-muted)',
        fontWeight: '500',
      }}
    >
      Fichier
    </div>
  );
});

/* ============================
   Caches locaux (localStorage)
============================ */
// Logos 7j (+ cache metadata/URL 24h)
const urlCacheKey = (fullPath) => `dlurl_${fullPath}`;
const metaCacheKey = (fullPath) => `meta_${fullPath}`;
const indexCacheKey = (uid) => PROMO_INDEX_CACHE_KEY(uid);

const loadIndexCache = (uid) => {
  if (!uid) return null;
  try {
    const raw = localStorage.getItem(indexCacheKey(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (parsed.ts && nowMs() - parsed.ts > PROMO_INDEX_TTL_LOCAL_MS) return null;
    return Array.isArray(parsed.ids) ? parsed.ids : null;
  } catch {
    return null;
  }
};

const saveIndexCache = (uid, ids) => {
  if (!uid) return;
  try {
    localStorage.setItem(indexCacheKey(uid), JSON.stringify({ ts: nowMs(), ids }));
  } catch {}
};

const getDownloadURLCached = async (itemRef) => {
  const key = urlCacheKey(itemRef.fullPath || itemRef._location?.path || '');
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const { url, ts } = JSON.parse(raw);
      if (url && ts && nowMs() - ts < URL_TTL_MS) return url;
    }
  } catch {}
  const url = await getDownloadURL(itemRef);
  try {
    localStorage.setItem(key, JSON.stringify({ url, ts: nowMs() }));
  } catch {}
  return url;
};

const getMetadataCached = async (itemRef) => {
  const key = metaCacheKey(itemRef.fullPath || itemRef._location?.path || '');
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const { meta, ts } = JSON.parse(raw);
      if (meta && ts && nowMs() - ts < META_TTL_MS) return meta;
    }
  } catch {}
  const meta = await getMetadata(itemRef);
  try {
    localStorage.setItem(key, JSON.stringify({ meta, ts: nowMs() }));
  } catch {}
  return meta;
};

async function getLatestLogoUrl(uid) {
  const cacheKey = `merchantLogo_${uid}`;
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const { url, ts } = JSON.parse(cached);
      if (url && ts && nowMs() - ts < LOGO_TTL_MS) return url;
    }
  } catch {}

  try {
    const profileRef = ref(storage, `${uid}/photo-profil`);
    const firstPage = await list(profileRef, { maxResults: 20 });
    if (firstPage.items.length === 0) {
      localStorage.removeItem(cacheKey);
      return '';
    }
    const itemsWithMeta = await Promise.all(
      firstPage.items.map(async (item) => {
        const meta = await getMetadataCached(item);
        return { item, t: new Date(meta.timeCreated).getTime() || 0 };
      })
    );
    itemsWithMeta.sort((a, b) => b.t - a.t);
    const url = await getDownloadURLCached(itemsWithMeta[0].item);
    localStorage.setItem(cacheKey, JSON.stringify({ url, ts: nowMs() }));
    return url;
  } catch {
    return '';
  }
}

/* ============================
   Full fetch
============================ */
async function buildMerchantsFromDocSnaps(docSnaps) {
  const now = new Date();
  const merchants = await Promise.all(
    docSnaps.map(async (docSnap) => {
      const merchantData = docSnap.data() || {};
      const uid =
        merchantData.userId ||
        merchantData.ownerUid ||
        merchantData.uid ||
        merchantData.userUID ||
        '';
      if (!uid) return null;

      let promos = [];
      try {
        const pubRef = ref(storage, `${uid}/pub`);
        const res = await listAll(pubRef);

        const itemsMeta = await Promise.all(
          res.items.map(async (item) => {
            const meta = await getMetadataCached(item);
            return { item, meta };
          })
        );

        const itemsFiltered = itemsMeta
          .map(({ item, meta }) => {
            const contentType = meta.contentType || '';
            const startRaw = meta.customMetadata?.start || '';
            const endRaw = meta.customMetadata?.end || '';
            const start = startRaw ? new Date(startRaw) : null;
            const end = endRaw && endRaw !== 'none' ? new Date(endRaw) : null;
            const created = new Date(meta.timeCreated);
            return { item, contentType, start, end, created };
          })
          .filter((pub) => pub.start && now >= pub.start && (!pub.end || now <= pub.end))
          .sort((a, b) => b.created - a.created);

        const withUrls = await Promise.all(
          itemsFiltered.map(async ({ item, contentType, start, end, created }) => {
            const url = await getDownloadURLCached(item);
            return { url, name: item.name, contentType, timeCreated: created, start, end };
          })
        );

        promos = withUrls;
      } catch {
        promos = [];
      }

      if (promos.length === 0) return null;

      const profilePhotoUrl = await getLatestLogoUrl(uid);
      const latestMs = promos[0]?.timeCreated?.getTime?.() || 0;

      return {
        uid, // merchant owner uid (Storage path)
        entrepriseId: docSnap.id,
        pseudo:
          merchantData.pseudo ||
          merchantData.entreprise_name ||
          merchantData.name ||
          merchantData.companyName ||
          '',
        profilePhotoUrl,
        promos,
        latestMs,
      };
    })
  );

  const withPromos = merchants.filter(Boolean);
  withPromos.sort((a, b) => (b.latestMs || 0) - (a.latestMs || 0));
  return withPromos;
}

/* ============================
   Component
============================ */
const ClientPromo = () => {
  const [merchantsPromos, setMerchantsPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line
  const [user, setUser] = useState(null);
  const indexPersistTimer = useRef(null);
  // keyRef gardé pour compat mais plus utilisé

  const navigate = useNavigate();
  const auth = getAuth();

  // Auth + user (kept minimal cost, pas de writes ici)
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userId = currentUser.uid;
        const userDocRef = doc(firestore, 'users', userId);
        const unsubscribeUser = onSnapshot(userDocRef, (docSnapshot) => {
          const userData = docSnapshot.data();
          setUser((prev) => {
            const next = { ...userData, uid: userId };
            if (prev && JSON.stringify(prev) === JSON.stringify(next)) return prev;
            return next;
          });
        });
        return () => unsubscribeUser();
      } else {
        navigate('/logingeneral');
      }
    });
    return () => unsubscribeAuth();
  }, [auth, navigate]);


  const handleViewPub = (pubUrl, logoUrl, pseudo) => {
    navigate(
      `/clientpubviewer?url=${encodeURIComponent(pubUrl)}&logo=${encodeURIComponent(logoUrl || '')}&pseudo=${encodeURIComponent(pseudo || '')}`
    );
  };

  const handleViewComplete = (merchant) => {
    navigate('/clientpubcomplet', { state: merchant });
  };

  const hasLoadedRef = useRef(false);
  const indexFreshRef = useRef(false);

  // Index promo côté client uniquement (pas de cache distant)
  const persistRemotePromoIndex = useCallback(() => {}, []);
  const loadRemotePromoIndex = useCallback(async () => null, []);

  const fetchAndSet = useCallback(
    async (docs, { skipLoader = false, uid = null, updateIndex = false } = {}) => {
      if (!skipLoader) setLoading(true);
      try {
        const fresh = await buildMerchantsFromDocSnaps(docs);
        setMerchantsPromos(fresh);
        if (updateIndex && uid) {
          const ids = docs.map((d) => d.id);
          saveIndexCache(uid, ids);
          persistRemotePromoIndex(uid, ids);
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Full promo fetch error:', e);
      } finally {
        if (!skipLoader) setLoading(false);
      }
    },
    [persistRemotePromoIndex]
  );

  // Écoute temps-réel des commerçants (promoUpdatedAt)
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const uid = getAuth().currentUser?.uid;
      const localIndex = uid ? loadIndexCache(uid) : null;
      const pickIndex = () => {
        const localFresh = localIndex && localIndex.length;
        if (localFresh) {
          indexFreshRef.current = true;
          return localIndex;
        }
        indexFreshRef.current = false;
        return null;
      };

      const indexIds = pickIndex();

      if (indexIds && indexIds.length) {
        // Fetch only indexed merchants
        const docSnaps = await Promise.all(
          indexIds.map(async (id) => {
            const s = await getDoc(doc(firestore, 'entreprises', id));
            return s.exists() ? s : null;
          })
        );
        const filtered = docSnaps.filter(Boolean);
        await fetchAndSet(filtered, { skipLoader: hasLoadedRef.current, uid, updateIndex: false });
        hasLoadedRef.current = true;
        return;
      }

      // Index absent/stale → scan borné et reconstruire
      const qMerchants = query(collection(firestore, 'entreprises'), fsLimit(ENTREPRISE_LIST_LIMIT));
      const snap = await getDocs(qMerchants);
      if (cancelled) return;
      await fetchAndSet(snap.docs, { skipLoader: hasLoadedRef.current, uid, updateIndex: true });
      hasLoadedRef.current = true;
    };

    run();
    return () => {
      cancelled = true;
      if (indexPersistTimer.current) {
        clearTimeout(indexPersistTimer.current);
        indexPersistTimer.current = null;
      }
    };
  }, [fetchAndSet, loadRemotePromoIndex]);

  return (
    <div className="dashboard-container cp-page">
      <ClientNavbar />
      <style>{CP_STYLES}</style>

      <div className="cp-inner">
        <header className="eb-page-header">
          <div>
            <h1 className="eb-page-title cp-title">Promotions</h1>
          </div>
        </header>

        <div className="cp-grid">
          {loading && (
            <div className="cp-state">
              <span className="eb-spinner" aria-hidden="true" />
              Chargement des promotions...
            </div>
          )}

          {!loading && merchantsPromos.length === 0 && (
            <div className="cp-state">
              Aucune promotion active pour le moment.
            </div>
          )}

          {merchantsPromos.map((merchant) => (
            <div key={merchant.uid} className="cp-card">
              <div className="cp-card__head">
                {merchant.profilePhotoUrl ? (
                  <img
                    loading="lazy"
                    src={merchant.profilePhotoUrl}
                    alt="Logo"
                    className="cp-card__logo"
                  />
                ) : (
                  <span className="cp-card__logo cp-card__logo--empty" aria-hidden="true">
                    {(merchant.pseudo || '?').trim().charAt(0)}
                  </span>
                )}
                <h2 className="cp-card__name" title={merchant.pseudo}>
                  {merchant.pseudo}
                </h2>
              </div>

              <div className="cp-thumbs">
                {merchant.promos.slice(0, 3).map((pub, idx) => (
                  <div
                    key={idx}
                    className="cp-thumb"
                    onClick={() => handleViewPub(pub.url, merchant.profilePhotoUrl, merchant.pseudo)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleViewPub(pub.url, merchant.profilePhotoUrl, merchant.pseudo);
                      }
                    }}
                    tabIndex={0}
                    role="button"
                    title={pub.name || 'Promotion'}
                  >
                    <Thumb pub={pub} />
                  </div>
                ))}
              </div>

              {merchant.promos.length > 3 && (
                <button
                  type="button"
                  className="eb-btn eb-btn--secondary eb-btn--block cp-more"
                  onClick={() => handleViewComplete(merchant)}
                >
                  Voir plus ({merchant.promos.length - 3} autres)
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CP_STYLES = `
.dashboard-container.cp-page { align-items: stretch; }
.cp-inner { width: 100%; max-width: var(--eb-content-max); margin: 0 auto; }
.dashboard-container .cp-title { text-align: left; margin: 0; font-size: 26px; }
.cp-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 320px), 1fr));
  gap: 20px;
}
.cp-state {
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 48px 20px;
  text-align: center;
  color: var(--eb-muted);
  font-size: 15px;
  background: var(--eb-surface);
  border: 1px solid var(--eb-border);
  border-radius: var(--eb-radius-lg);
  box-shadow: var(--eb-shadow-sm);
}
.cp-card {
  min-width: 0;
  padding: 20px;
  background: var(--eb-surface);
  border: 1px solid var(--eb-border);
  border-radius: var(--eb-radius-lg);
  box-shadow: var(--eb-shadow-sm);
  transition: box-shadow .15s ease, border-color .15s ease;
}
.cp-card:hover { box-shadow: var(--eb-shadow-md); border-color: var(--eb-border-strong); }
.cp-card__head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--eb-border);
  min-width: 0;
}
.cp-card__logo {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid var(--eb-border);
}
.cp-card__logo--empty {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--eb-brand-50);
  color: var(--eb-brand);
  font-weight: 700;
  font-size: 18px;
  text-transform: uppercase;
}
.dashboard-container h2.cp-card__name {
  margin: 0;
  min-width: 0;
  text-align: left;
  font-size: 17px;
  font-weight: 650;
  color: var(--eb-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.01em;
}
.cp-thumbs { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.cp-thumb {
  cursor: pointer;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  border-radius: var(--eb-radius);
  background: var(--eb-surface-2);
  border: 1px solid var(--eb-border);
  -webkit-tap-highlight-color: transparent;
  transition: transform .15s ease, box-shadow .15s ease;
}
.cp-thumb:hover { transform: scale(1.03); box-shadow: var(--eb-shadow-md); }
.cp-thumb:focus-visible { outline: none; box-shadow: var(--eb-ring); }
.cp-more { margin-top: 16px; }
@media (max-width: 600px) {
  .dashboard-container .cp-title { font-size: 22px; }
  .cp-grid { gap: 16px; }
  .cp-card { padding: 16px; }
}
`;

export default ClientPromo;
